// Document validation utilities with correct mandatory rules
export const getMandatoryDocuments = (formData) => {
  const country = formData.countryOrigin;
  
  // Digital signature is ALWAYS mandatory for all vendors
  const mandatory = ['digitalSignature'];
  
  if (country === 'IN') {
    mandatory.push('bankProof', 'panDocument', 'gstDocument');
    
    // Add MSME-related documents based on status
    if (formData.msmeStatus === 'registered') {
      // When registered, MSME Certificate is required
      mandatory.push('msmeCertificate');
    } else if (formData.msmeStatus === 'not-registered') {
      // When not registered, MSME Declaration is required
      mandatory.push('msmeDeclarationFile');
    }
    // If pending, no additional MSME document required
    
    return mandatory;
  } else {
    // For non-Indian vendors
    mandatory.push('bankProof', 'incorporationCertificate');
    return mandatory;
  }
};

// Map formData field names to uploadedFiles keys
const getUploadedFileKey = (docType) => {
  const mapping = {
    'digitalSignature': 'DIGITAL_SIGNATURE',
    'bankProof': 'BANK_PROOF',
    'panDocument': 'PAN_CARD',
    'gstDocument': 'GST_CERTIFICATE',
    'msmeCertificate': 'MSME_CERTIFICATE',
    'msmeDeclarationFile': 'MSME_DECLARATION',
    'incorporationCertificate': 'INCORPORATION_CERTIFICATE',
  };
  return mapping[docType] || docType.toUpperCase();
};
  
/** The step each document is uploaded on, so a link that opens on some steps only asks for their documents. */
const DOCUMENT_STEP = {
  digitalSignature: 'agreements',
  bankProof: 'bank',
  panDocument: 'compliance',
  gstDocument: 'compliance',
  msmeCertificate: 'categorization',
  msmeDeclarationFile: 'categorization',
  incorporationCertificate: 'company',
};

/** `onlySteps`: the step ids on screen — documents belonging to other steps are not demanded. */
export const validateMandatoryDocuments = (formData, keepExistingDocuments = {}, uploadedFiles = {}, { onlySteps = null } = {}) => {
  const mandatoryDocs = getMandatoryDocuments(formData).filter((doc) => {
    if (!onlySteps) return true;
    const step = DOCUMENT_STEP[doc];
    // The MSME step is 'msme' at registration and 'categorization' in regularization; both hold the MSME documents.
    return onlySteps.has(step) || (step === 'categorization' && onlySteps.has('msme'));
  });
  const missingDocs = [];
  const invalidDocs = [];

  // Debug logging
  console.log('🔍 Document Validation Debug:');
  console.log('Country Origin:', formData.countryOrigin);
  console.log('MSME Status:', formData.msmeStatus);
  console.log('MSME Category:', formData.msmeCategory);
  console.log('Mandatory Documents:', mandatoryDocs);
  console.log('Form Data Files:', {
    digitalSignature: !!formData.digitalSignature,
    bankProof: !!formData.bankProof,
    panDocument: !!formData.panDocument,
    gstDocument: !!formData.gstDocument,
    msmeCertificate: !!formData.msmeCertificate,
    msmeDeclarationFile: !!formData.msmeDeclarationFile,
    incorporationCertificate: !!formData.incorporationCertificate
  });
  console.log('Keep Existing Documents:', keepExistingDocuments);
  console.log('Uploaded Files:', Object.keys(uploadedFiles));

  mandatoryDocs.forEach(docType => {
    const file = formData[docType];
    
    // Check if keeping existing document
    const isKeepingExisting = keepExistingDocuments[docType];
    const uploadedFileKey = getUploadedFileKey(docType);
    const hasUploadedFile = uploadedFiles[uploadedFileKey];
    
    // Already on the server: kept on purpose, or restored into the form as the server's own reference on
    // resume ({ id, file_name, … } — not a File, so validateFile below would call it invalid).
    const isServerReference = Boolean(file) && !(file instanceof File) && file.id != null;
    if (hasUploadedFile && (isKeepingExisting || isServerReference)) {
      // Valid: keeping existing document
      console.log(`✅ Valid (keeping existing): ${docType}`);
      return;
    }
    
    if (!file) {
      missingDocs.push(docType);
      console.log(`❌ Missing: ${docType}`);
    } else if (!validateFile(file, docType)) {
      invalidDocs.push(docType);
      console.log(`⚠️ Invalid: ${docType}`);
    } else {
      console.log(`✅ Valid: ${docType}`);
    }
  });

  const result = {
    isValid: missingDocs.length === 0 && invalidDocs.length === 0,
    missingDocs,
    invalidDocs,
    mandatoryDocs,
    errors: [
      ...missingDocs.map(doc => `${getDocumentTypeName(doc)} is required`),
      ...invalidDocs.map(doc => `${getDocumentTypeName(doc)} is invalid`)
    ]
  };

  console.log('📋 Validation Result:', result);
  return result;
};
  
  // Document-specific size limits (must match backend constants)
  const FILE_SIZE_LIMITS = {
    digitalSignature: 10 * 1024, // 10KB
    bankProof: 4 * 1024 * 1024, // 4MB
    panDocument: 2 * 1024 * 1024, // 2MB
    gstDocument: 3 * 1024 * 1024, // 3MB
    msmeCertificate: 4 * 1024 * 1024, // 4MB
    msmeDeclarationFile: 4 * 1024 * 1024, // 4MB
    incorporationCertificate: 4 * 1024 * 1024, // 4MB
    complianceCertificate: 3 * 1024 * 1024, // 3MB (default)
  };

  export const validateFile = (file, docType) => {
    if (!file) return false;
    if (!(file instanceof File)) return false;
    if (file.size === 0) return false;
    
    // Get document-specific size limit
    const maxSize = FILE_SIZE_LIMITS[docType] || (3 * 1024 * 1024); // Default 3MB
    
    if (file.size > maxSize) {
      console.error(`File size ${file.size} exceeds limit ${maxSize} for ${docType}`);
      return false;
    }
    
    // Digital signature: only images
    if (docType === 'digitalSignature') {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      return allowedTypes.includes(file.type);
    }
    
    // Other documents: images or PDFs
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    return allowedTypes.includes(file.type);
  };

  // Helper function to get formatted size limit for display
  export const getFileSizeLimit = (docType) => {
    const sizeInBytes = FILE_SIZE_LIMITS[docType] || (3 * 1024 * 1024);
    const sizeInKB = sizeInBytes / 1024;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    
    if (sizeInKB < 1024) {
      return `${sizeInKB}KB`;
    }
    return `${sizeInMB}MB`;
  };
  
  export const createDocumentUploadQueue = (formData) => {
    const uploads = [];
    const mandatoryDocs = getMandatoryDocuments(formData);
  
    // Add mandatory documents
    mandatoryDocs.forEach(docType => {
      const file = formData[docType];
      if (file) {
        uploads.push({
          type: docType,
          file,
          mandatory: true,
          status: 'pending'
        });
      }
    });
  
    // Add optional documents (only if they exist)
    const optionalDocs = ['complianceCertificate'];
    optionalDocs.forEach(docType => {
      const file = formData[docType];
      if (file) {
        uploads.push({
          type: docType,
          file,
          mandatory: false,
          status: 'pending'
        });
      }
    });
  
    return uploads;
  };
  
export const getDocumentTypeName = (docType) => {
  const names = {
    'digitalSignature': 'Digital Signature',
    'bankProof': 'Bank Cancelled Check',
    'panDocument': 'PAN Document',
    'gstDocument': 'GST Document',
    'msmeCertificate': 'MSME Certificate',
    'msmeDeclarationFile': 'MSME Declaration',
    'incorporationCertificate': 'Incorporation Certificate',
    'complianceCertificate': 'Compliance Certificate'
  };
  return names[docType] || docType;
};